import { useEffect, useState } from 'react';
import bs58 from 'bs58';
import { ethers, providers, Wallet } from 'ethers';
import { Connection, Keypair, PublicKey } from '@solana/web3.js';

import * as Form from '../Components/form';
import { Section } from '../Components/Section';
import { SolanaIcon } from '../Components/icons/SolanaIcon';
import { EthereumIcon, EthereumCoinsIcon } from '../Components/icons/EthereumIcon';
import { TelegramIcon } from '../Components/icons/TelegramIcon';
import { WalletState } from '../Components/WalletState';
import { Status } from '../Components/Status';
import { ArrowRightIcon } from '../Components/icons/ArrowRightIcon';
import { PlusIcon } from '../Components/icons/PlusIcon';
import SolWalletGenerator from '../Components/SolWalletGenerator';
import { collectEthMultiple, disperseEthMultiple } from '../utils/ethereum/ERC20Token';
import { getSolanaWallet } from '../utils/solana/wallet';
import { getEthereumWallet } from '../utils/ethereum/wallet';
import { getWalletsTokens } from '../utils/solana/get-wallet-tokens';
import { useBackend } from '../Hooks/UseBackend';
import { TelegramSettings } from '../telegram/types';
import { TelegramConnectionStatus, useTelegram } from '../telegram';
import { RefWrapper } from '../Components/RefWrapper';
import { Spinner } from '../Components/Spinner';

const hidePrivateKey = (value: string, masked: boolean): string => {
    return masked ? '*'.repeat(value.length) : value;
};

export type UserSettings = TelegramSettings & {
    solInitialBuyArrow: number;
    solInitialBuyCopy: number;
    ethLiquidityArrow: number;
    ethLiquidityCopy: number;
    solSnipeArrow: number;
    solSnipeCopy: number;
    ethSnipeValues: number[];
    solBuyTxCost: number;
    solSellTxCost: number;
    autoSellDelayArrow: number;
    autoSellProfitArrow: number;
    autoSellDelayCreation: number;
    autoSellProfitCreation: number;
    ethPrivateKey: string;
    solanaPrivateKey: string;
    solanaSniperPrivateKey: string;
    ethSniperPrivateKeys: string[];
    ethDisperserPrivateKey: string;
    zeroSlotSellTip: number;
    zeroSlotBuyTip: number;
    solRpcUrl: string;
    ethRpcUrl: string;
    purchasedSolTokens: string[];
    purchasedSolTokensNames: string[];
    purchasedSolTokensSymbols: string[];
    purchasedSolTokensImages: string[];
    deployedSolTokens: string[];
    deployedSolTokensNames: string[];
    deployedSolTokensSymbols: string[];
    deployedSolTokensImages: string[];
};

export function SettingsPage({
    solInitialBuyArrow,
    onSolInitialBuyArrowChange,
    solInitialBuyCopy,
    onSolInitialBuyCopyChange,

    ethLiquidityArrow,
    onEthLiquidityArrowChange,
    ethLiquidityCopy,
    onEthLiquidityCopyChange,

    solSnipeArrow,
    onSolSnipeArrowChange,
    solSnipeCopy,
    onSolSnipeCopyChange,

    ethSnipeValues,
    onEthSnipeValuesChange,

    solBuyTxCost,
    onSolBuyTxCostChange,
    solSellTxCost,
    onSolSellTxCostChange,

    autoSellDelayArrow,
    onAutoSellDelayArrowChange,
    autoSellProfitArrow,
    onAutoSellProfitArrowChange,

    autoSellDelayCreation,
    onAutoSellDelayCreationChange,
    autoSellProfitCreation,
    onAutoSellProfitCreationChange,
    ethPrivateKey,
    onEthPrivateKeyChange,
    solanaPrivateKey,
    onSolanaPrivateKeyChange,
    solanaSniperPrivateKey,
    onSolanaSniperPrivateKeyChange,
    ethSniperPrivateKeys,
    onEthSniperPrivateKeysChange,
    ethDisperserPrivateKey,
    onEthDisperserPrivateKeyChange,
    zeroSlotSellTip,
    onZeroSlotSellTipChange,
    zeroSlotBuyTip,
    onZeroSlotBuyTipChange,

    solRpcUrl,
    onSolRpcUrlChange,
    solConnection,

    ethRpcUrl,
    onEthRpcUrlChange,
    ethProvider,

    onSetPurchasedSolTokens,
    onSetDeployedSolTokens,

    telegramAPIID,
    onTelegramAPIIDChange,
    telegramAPIHash,
    onTelegramAPIHashChange,
    telegramPhoneNumber,
    onTelegramPhoneNumberChange,
}: UserSettings & {
    solConnection: Connection;
    ethProvider: providers.JsonRpcProvider;

    onSolInitialBuyArrowChange: (value: number) => void;
    onSolInitialBuyCopyChange: (value: number) => void;
    onEthLiquidityArrowChange: (value: number) => void;
    onEthLiquidityCopyChange: (value: number) => void;
    onSolSnipeArrowChange: (value: number) => void;
    onSolSnipeCopyChange: (value: number) => void;
    onEthSnipeValuesChange: (values: number[]) => void;
    onSolBuyTxCostChange: (value: number) => void;
    onSolSellTxCostChange: (value: number) => void;
    onAutoSellDelayArrowChange: (value: number) => void;
    onAutoSellProfitArrowChange: (value: number) => void;
    onAutoSellDelayCreationChange: (value: number) => void;
    onAutoSellProfitCreationChange: (value: number) => void;
    onEthPrivateKeyChange: (value: string) => void;
    onSolanaPrivateKeyChange: (value: string) => void;
    onSolanaSniperPrivateKeyChange: (value: string) => void;
    onEthSniperPrivateKeysChange: (value: string[]) => void;
    onEthDisperserPrivateKeyChange: (value: string) => void;
    onZeroSlotSellTipChange: (value: number) => void;
    onZeroSlotBuyTipChange: (value: number) => void;
    onSolRpcUrlChange: (url: string) => void;
    onEthRpcUrlChange: (url: string) => void;
    onSetDeployedSolTokens: (tokens: string[], names: string[], symbols: string[], images: string[]) => void;
    onSetPurchasedSolTokens: (tokens: string[], names: string[], symbols: string[], images: string[]) => void;
    onTelegramAPIIDChange: (apiID: string | null) => void;
    onTelegramAPIHashChange: (apiHash: string | null) => void;
    onTelegramPhoneNumberChange: (phone: string | null) => void;
}) {
    const { getTokenMetadata } = useBackend();
    const [ethDisperseAmount, onEthDisperseAmountChange] = useState<number>(0.05);

    const [solRpcUrlInputValue, onSolRpcUrlInputValueChange] = useState<string>(solRpcUrl);
    const [ethRpcUrlInputValue, onEthRpcUrlInputValueChange] = useState<string>(ethRpcUrl);

    console.log('settings.ethSniperPrivateKeys', ethSniperPrivateKeys);
    console.log('settings.ethSnipeValues', ethSnipeValues);
    const [ethSnipersInputValue, setEthSnipersInputValue] = useState<string>(
        ethSniperPrivateKeys.map((pk, i) => `${pk},${ethSnipeValues[i] ?? ''}`).join('\n'),
    );

    const [isKeyValid, setIsKeyValid] = useState({
        solana: false,
        solanaSniper: false,
        eth: false,
        ethSnipers: false,
        ethDisperser: false,
    });

    const [isKeyMasked, setIsKeyMasked] = useState({
        solana: false,
        solanaSniper: false,
        eth: false,
        ethSnipers: false,
        ethDisperser: false,
    });

    useEffect(() => {
        const updatedValid = { ...isKeyValid };
        const updatedMasked = { ...isKeyMasked };

        // ETH
        try {
            new Wallet(ethPrivateKey, new providers.JsonRpcProvider(ethRpcUrl));
            updatedValid.eth = true;
            updatedMasked.eth = true;
        } catch {
            updatedValid.eth = false;
            updatedMasked.eth = false;
        }

        // ETH Disperser
        try {
            new Wallet(ethDisperserPrivateKey, ethProvider);
            updatedValid.ethDisperser = true;
            updatedMasked.ethDisperser = true;
        } catch {
            updatedValid.ethDisperser = false;
            updatedMasked.ethDisperser = false;
        }

        // Solana
        try {
            Keypair.fromSecretKey(bs58.decode(solanaPrivateKey));
            updatedValid.solana = true;
            updatedMasked.solana = true;
        } catch {
            updatedValid.solana = false;
            updatedMasked.solana = false;
        }

        // Solana Sniper
        try {
            Keypair.fromSecretKey(bs58.decode(solanaSniperPrivateKey));
            updatedValid.solanaSniper = true;
            updatedMasked.solanaSniper = true;
        } catch {
            updatedValid.solanaSniper = false;
            updatedMasked.solanaSniper = false;
        }

        setIsKeyValid(updatedValid);
        setIsKeyMasked(updatedMasked);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ethPrivateKey, ethDisperserPrivateKey, solanaPrivateKey, solanaSniperPrivateKey, ethProvider]);

    const onEthSnipersInputValueChange = (value: string) => {
        try {
            setEthSnipersInputValue(value);

            const rows = value.split('\n').map((row) => {
                const [privateKeyRaw, snipeValueRaw] = row.trim().split(',');
                return { privateKey: privateKeyRaw?.trim(), snipeValue: snipeValueRaw?.trim() };
            });

            const privateKeys = rows.map((r) => r.privateKey).filter((pk) => pk && pk.length > 0);
            const snipeValues = rows.map((r) => (r.snipeValue && r.snipeValue.length > 0 ? Number(r.snipeValue) : null)).filter((v) => v !== null) as number[];

            if (privateKeys.length !== snipeValues.length) {
                setIsKeyValid((prev) => ({ ...prev, ethSnipers: false }));
                return;
            }

            for (const key of privateKeys) {
                try {
                    new Wallet(key, new providers.JsonRpcProvider(ethRpcUrl));
                } catch {
                    setIsKeyValid((prev) => ({ ...prev, ethSnipers: false }));
                    return;
                }
            }

            for (const val of snipeValues) {
                if (val === null || isNaN(val) || val <= 0 || val > 100) {
                    setIsKeyValid((prev) => ({ ...prev, ethSnipers: false }));
                    return;
                }
            }

            onEthSniperPrivateKeysChange(privateKeys);
            onEthSnipeValuesChange(snipeValues);
            setIsKeyValid((prev) => ({ ...prev, ethSnipers: true }));
        } catch {
            setIsKeyValid((prev) => ({ ...prev, ethSnipers: false }));
        }
    };

    async function syncTokens() {
        const addresses: PublicKey[] = [solanaPrivateKey, solanaSniperPrivateKey]
            .filter(Boolean)
            .map((pk) => Keypair.fromSecretKey(bs58.decode(pk!)).publicKey);

        const { deployedTokens, purchasedTokens } = await getWalletsTokens(solConnection, addresses);

        async function fetchTokenData(tokens: PublicKey[]) {
            const tokenAddresses: string[] = [];
            const tokenNames: string[] = [];
            const tokenSymbols: string[] = [];
            const tokenImages: string[] = [];

            for (const token of tokens) {
                try {
                    const base58 = token.toBase58();
                    const metadata = await getTokenMetadata(base58);

                    tokenAddresses.push(base58);
                    tokenNames.push(metadata.name);
                    tokenSymbols.push(metadata.symbol);
                    tokenImages.push(metadata.image ?? '');
                } catch (e) {
                    console.log('Failed to fetch metadata for token:', token.toBase58(), e);
                }
            }

            return { tokenAddresses, tokenNames, tokenSymbols, tokenImages };
        }

        const deployedData = await fetchTokenData(deployedTokens);
        onSetDeployedSolTokens(deployedData.tokenAddresses, deployedData.tokenNames, deployedData.tokenSymbols, deployedData.tokenImages);

        const purchasedData = await fetchTokenData(purchasedTokens);
        onSetPurchasedSolTokens(purchasedData.tokenAddresses, purchasedData.tokenNames, purchasedData.tokenSymbols, purchasedData.tokenImages);
    }

    const telegram = useTelegram({
        telegramAPIID,
        telegramAPIHash,
        telegramPhoneNumber,
    });

    return (
        <div className='flex'>
            <div className='min-w-[363px] flex flex-col border border-midnight'>
                <Section
                    icon={<SolanaIcon className='size-[20px]' />}
                    title={<span className='font-bold'>Solana RPC/Connections</span>}
                    bodyClassName='w-full p-[8px]'
                >
                    <form className='w-full flex flex-col gap-[18px]'>
                        <div className='w-full flex gap-[8px]'>
                            <label className='flex items-center'>
                                <span className='w-[45px] text-right whitespace-normal mr-[15px]'>Preferred Chart</span>
                                <Form.Select className='h-[25px] w-[96px] text-[10px] font-medium'>
                                    <option>DexScreener</option>
                                </Form.Select>
                            </label>
                            <label className='flex flex-1 items-center'>
                                <span className='w-[69px] text-right whitespace-normal mr-[15px]'>Preferred Trading Bot</span>
                                <Form.Select className='h-[25px] w-[89px] text-[10px] font-medium'>
                                    <option>Axiom</option>
                                </Form.Select>
                            </label>
                        </div>
                        <div>
                            <label className='flex items-center'>
                                <span className='w-[45px] text-right whitespace-normal mr-[15px]'>Solana RPC</span>
                                <Form.Input className='flex-1' value={solRpcUrlInputValue} onChange={(e) => onSolRpcUrlInputValueChange(e.target.value)} />
                                <SaveButton onClick={() => onSolRpcUrlChange(solRpcUrlInputValue)} />
                            </label>
                        </div>
                        <div className='flex gap-[18px]'>
                            <div className='flex flex-col gap-[22px]'>
                                <label className='flex items-center'>
                                    <span className='w-[45px] text-right whitespace-normal mr-[15px]'>Buy Prio Fee (SOL)</span>
                                    {/* TODO: Make a better SOL -> priority fee calculator */}
                                    <Form.Float value={solBuyTxCost} onChange={(e) => onSolBuyTxCostChange(e.target.value)} className='flex-1' />
                                </label>
                                <label className='flex items-center'>
                                    <span className='w-[45px] text-right whitespace-normal mr-[15px]'>0Slot Buy Tip</span>
                                    <Form.Float
                                        className='flex-1'
                                        min={0.0001}
                                        value={zeroSlotBuyTip}
                                        onChange={(e) => onZeroSlotBuyTipChange(e.target.value)}
                                    />
                                </label>
                            </div>
                            <div className='flex flex-col gap-[22px]'>
                                <label className='flex items-center'>
                                    <span className='w-[45px] text-right whitespace-normal mr-[15px]'>Sell Prio Fee (SOL)</span>
                                    <Form.Float value={solSellTxCost} onChange={(e) => onSolSellTxCostChange(e.target.value)} className='flex-1' />
                                </label>
                                <label className='flex items-center'>
                                    <span className='w-[45px] text-right whitespace-normal mr-[15px]'>0Slot Sell Tip</span>
                                    <Form.Float
                                        className='flex-1'
                                        min={0.0001}
                                        value={zeroSlotSellTip}
                                        onChange={(e) => onZeroSlotSellTipChange(e.target.value)}
                                    />
                                </label>
                            </div>
                            <div className='flex flex-col justify-center'>
                                <SaveButton />
                            </div>
                        </div>
                    </form>
                </Section>
                <Section
                    icon={<EthereumIcon className='size-[20px] filter invert' />}
                    title={<span className='font-bold'>Ethereum RPC</span>}
                    bodyClassName='w-full p-[8px]'
                >
                    <form className='w-full flex justify-between gap-[10px]'>
                        <label className='w-full flex items-center'>
                            <span className='w-[45px] text-right whitespace-normal mr-[15px]'>ETH RPC</span>
                            <Form.Input value={ethRpcUrlInputValue} onChange={(e) => onEthRpcUrlInputValueChange(e.target.value)} className='w-full' />
                        </label>
                        <SaveButton onClick={() => onEthRpcUrlChange(ethRpcUrlInputValue)} />
                    </form>
                </Section>
                <Section
                    icon={<TelegramIcon className='size-[20px]' />}
                    title={<span className='font-bold'>Telegram Connection</span>}
                    bodyClassName='w-full p-[8px]'
                >
                    <form className='w-full flex flex-col gap-[7px]'>
                        <div className='flex justify-between gap-[10px]'>
                            <label className='w-full flex items-center'>
                                <span className='w-[45px] shrink-0 text-right whitespace-normal mr-[15px]'>API ID</span>
                                <Form.Input
                                    className='w-full'
                                    value={telegramAPIID ?? ''}
                                    onChange={(event) => {
                                        if (event.target.value) {
                                            onTelegramAPIIDChange(event.target.value);
                                        } else {
                                            onTelegramAPIIDChange(null);
                                        }
                                    }}
                                />
                            </label>
                            <SaveButton onClick={telegram.sendCode} />
                        </div>
                        <div className='flex justify-start'>
                            <label className='flex items-center'>
                                <span className='w-[45px] text-right whitespace-normal mr-[15px]'>API Hash</span>
                                <Form.Input
                                    className='w-[221px]'
                                    value={telegramAPIHash ?? ''}
                                    onChange={(event) => {
                                        if (event.target.value) {
                                            onTelegramAPIHashChange(event.target.value);
                                        } else {
                                            onTelegramAPIHashChange(null);
                                        }
                                    }}
                                />
                            </label>
                        </div>
                        <div className='flex justify-start'>
                            <label className='flex items-center'>
                                <span className='w-[45px] text-right whitespace-normal mr-[15px]'>Phone number</span>
                                <Form.Input
                                    className='w-[221px]'
                                    inputMode='numeric'
                                    pattern='[0-9]*'
                                    value={telegramPhoneNumber ?? ''}
                                    onChange={(event) => {
                                        if (event.target.value) {
                                            onTelegramPhoneNumberChange(event.target.value.replace(/\D/g, ''));
                                        } else {
                                            onTelegramPhoneNumberChange(null);
                                        }
                                    }}
                                />
                            </label>
                        </div>
                        {telegram.authorized === false && telegram.validEnv && (
                            <RefWrapper<HTMLInputElement>>
                                {(ref) => (
                                    <div className='flex justify-between gap-[10px]'>
                                        <label className='w-full flex items-center'>
                                            <span className='w-[45px] shrink-0 text-right whitespace-normal mr-[15px]'>Code</span>
                                            <Form.Input ref={ref} className='w-full' />
                                        </label>
                                        <Form.Button
                                            className='w-[54px] h-[34px] text-[14px] text-ash-grey font-bold'
                                            onClick={() => {
                                                ref.current?.value && telegram.signIn(ref.current.value);
                                            }}
                                        >
                                            Send
                                        </Form.Button>
                                    </div>
                                )}
                            </RefWrapper>
                        )}
                        <div className='m-auto'>
                            {telegram.authorized === undefined ? <Spinner /> : <TelegramConnectionStatus connected={telegram.authorized} />}
                        </div>
                    </form>
                </Section>
            </div>
            <div className='w-[532px] flex flex-col border border-midnight border-x-0'>
                <Section icon={<SolanaIcon className='size-[20px]' />} title={<span className='font-bold'>Solana Wallets</span>} bodyClassName='w-full'>
                    <div>
                        <div className='flex flex-col gap-[10px] border-b border-midnight p-[18px]'>
                            <form className='w-full flex justify-between items-center gap-[10px]'>
                                <label className='w-full flex items-center'>
                                    <span className='w-[79px] text-right whitespace-normal mr-[15px]'>SOL Dev Wallet Key</span>
                                    <Form.Input
                                        value={hidePrivateKey(solanaPrivateKey, isKeyMasked.solana)}
                                        onChange={(e) => onSolanaPrivateKeyChange(e.target.value)}
                                        className='w-full'
                                    />
                                </label>
                                <Status className='shrink-0' status={isKeyValid.solana ? 'ok' : 'notOk'} statusMap={Status.defaultStatusMap} />
                                <ClearButton
                                    onClick={() => {
                                        onSolanaPrivateKeyChange('');
                                        onSetDeployedSolTokens([], [], [], []);
                                    }}
                                />
                                {solanaPrivateKey ? null : <SolWalletGenerator onPrivateKeyChange={onSolanaPrivateKeyChange} />}
                                <button
                                    type='button'
                                    onClick={() => {
                                        syncTokens();
                                    }}
                                >
                                    Sync Tokens
                                </button>
                            </form>
                            <div className='flex gap-[10px]'>
                                <SolanaIcon className='size-[20px] filter brightness-0 invert' />
                                <WalletState
                                    currency='SOL'
                                    getWalletStatus={() => getSolanaWallet(solanaPrivateKey, solConnection)}
                                    getScreenerUrl={(walletAddress) => `https://solscan.io/account/${walletAddress}`}
                                />
                            </div>
                        </div>
                        <div className='flex flex-col gap-[10px] p-[18px]'>
                            <form className='w-full flex justify-between items-center gap-[10px]'>
                                <label className='w-full flex items-center'>
                                    <span className='w-[79px] text-[10px] text-right leading-none mr-[15px]'>SOL Buyer & Sniper Wallet Key</span>
                                    <Form.Input
                                        value={hidePrivateKey(solanaSniperPrivateKey, isKeyMasked.solanaSniper)}
                                        onChange={(e) => onSolanaSniperPrivateKeyChange(e.target.value)}
                                        className='w-full'
                                    />
                                </label>
                                <Status className='shrink-0' status={isKeyValid.solanaSniper ? 'ok' : 'notOk'} statusMap={Status.defaultStatusMap} />
                                <ClearButton
                                    onClick={() => {
                                        onSolanaSniperPrivateKeyChange('');
                                        onSetPurchasedSolTokens([], [], [], []);
                                    }}
                                />
                                {solanaSniperPrivateKey ? null : <SolWalletGenerator onPrivateKeyChange={onSolanaSniperPrivateKeyChange} />}
                            </form>
                            <div className='flex gap-[10px]'>
                                <SolanaIcon className='size-[20px] filter brightness-0 invert' />
                                <WalletState
                                    currency='SOL'
                                    getWalletStatus={() => getSolanaWallet(solanaSniperPrivateKey, solConnection)}
                                    getScreenerUrl={(walletAddress) => `https://solscan.io/account/${walletAddress}`}
                                />
                            </div>
                        </div>
                    </div>
                </Section>
                <Section
                    icon={<EthereumIcon className='size-[20px] filter invert' />}
                    title={<span className='font-bold'>Ethereum Wallets & Disperser Tool</span>}
                    bodyClassName='w-full'
                >
                    <div>
                        <div className='flex flex-col gap-[10px] border-b border-midnight p-[18px]'>
                            <form className='w-full flex justify-between items-center gap-[10px]'>
                                <label className='w-full flex items-center'>
                                    <span className='w-[79px] text-right whitespace-normal mr-[15px]'>ETH Dev Wallet Key</span>
                                    <Form.Input
                                        value={hidePrivateKey(ethPrivateKey, isKeyMasked.eth)}
                                        onChange={(e) => onEthPrivateKeyChange(e.target.value)}
                                        className='w-full'
                                    />
                                </label>
                                <Status className='shrink-0' status={isKeyValid.eth ? 'ok' : 'notOk'} statusMap={Status.defaultStatusMap} />
                                <ClearButton onClick={() => onEthPrivateKeyChange('')} />
                            </form>
                            <div className='flex gap-[10px]'>
                                <EthereumCoinsIcon className='size-[20px] filter invert' />
                                <WalletState
                                    currency='ETH'
                                    getWalletStatus={() => getEthereumWallet(ethPrivateKey, ethProvider)}
                                    getScreenerUrl={(address) => `https://etherscan.io/address/${address}`}
                                />
                            </div>
                        </div>
                        <div className='flex flex-col gap-[10px] border-b border-midnight p-[18px]'>
                            <form className='w-full flex justify-between items-center gap-[10px]'>
                                <label className='w-full flex items-center'>
                                    <span className='w-[79px] text-right whitespace-normal mr-[15px]'>ETH Multi Sniper Keys(PK,%)</span>
                                    <Form.Textarea
                                        className='h-[92px] w-full text-[8px] font-medium tracking-[-0.055em]'
                                        value={ethSnipersInputValue}
                                        placeholder={`pk1,2%\npk2,2%\npk3,2%\npk4,2%\npk5,2%`}
                                        onChange={(e) => {
                                            onEthSnipersInputValueChange(e.target.value);
                                        }}
                                    />
                                </label>
                                <Status className='shrink-0' status={isKeyValid.ethSnipers ? 'ok' : 'notOk'} statusMap={Status.defaultStatusMap} />
                                <ClearButton onClick={() => onEthSnipersInputValueChange('')} />
                            </form>
                            {ethSniperPrivateKeys.map((key, i) => (
                                <div key={key} className='flex items-center gap-[10px]'>
                                    <span className='font-bold'>{i + 1}</span>
                                    <WalletState
                                        currency='ETH'
                                        getWalletStatus={() => getEthereumWallet(key, ethProvider)}
                                        getScreenerUrl={(address) => `https://etherscan.io/address/${address}`}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className='flex flex-col gap-[10px] p-[18px]'>
                            <span className='font-medium'>Disperse ETH from Disperser Wallet to Multi Snipers</span>
                            <form className='w-full flex justify-between items-center gap-[10px]'>
                                <label className='w-full flex items-center'>
                                    <span className='w-[79px] text-right whitespace-normal mr-[15px]'>ETH Disperser Wallet Key</span>
                                    <Form.Input
                                        className='w-full'
                                        value={hidePrivateKey(ethDisperserPrivateKey, isKeyMasked.ethDisperser)}
                                        onChange={(e) => onEthDisperserPrivateKeyChange(e.target.value)}
                                    />
                                </label>
                                <Status className='shrink-0' status={isKeyValid.ethDisperser ? 'ok' : 'notOk'} statusMap={Status.defaultStatusMap} />
                                <ClearButton onClick={() => onEthDisperserPrivateKeyChange('')} />
                            </form>
                            <div className='flex items-center gap-[10px]'>
                                <EthereumCoinsIcon className='size-[20px] filter invert' />
                                <WalletState
                                    rootClassname='w-[195px]'
                                    currency='ETH'
                                    getWalletStatus={() => getEthereumWallet(ethDisperserPrivateKey, ethProvider)}
                                    getScreenerUrl={(address) => `https://etherscan.io/address/${address}`}
                                />
                            </div>
                            <div className='flex justify-between items-center'>
                                <Form.Float className='text-right' value={ethDisperseAmount} onChange={(e) => onEthDisperseAmountChange(e.target.value)} />
                                <span>ETH</span>
                                <span>to {ethSniperPrivateKeys.length} wallet[s]</span>
                                <div className='flex'>
                                    <Form.Button
                                        disabled={!isKeyValid.ethDisperser || !ethDisperseAmount || !ethSniperPrivateKeys.length}
                                        className='w-[138px] h-[40px] text-[14px] font-bold mr-[18px]'
                                        onClick={() => {
                                            disperseEthMultiple(
                                                new Wallet(ethDisperserPrivateKey, ethProvider),
                                                ethSniperPrivateKeys.map((pk) => new Wallet(pk, ethProvider).address),
                                                new Array(ethSniperPrivateKeys.length).fill(ethers.utils.parseEther(ethDisperseAmount.toString())),
                                                ethProvider,
                                            );
                                        }}
                                    >
                                        Disperse
                                    </Form.Button>
                                    <Form.Button
                                        disabled={!isKeyValid.ethDisperser || !ethDisperseAmount || !ethSniperPrivateKeys.length}
                                        className='w-[138px] h-[53px] font-bold !px-0'
                                        onClick={() => {
                                            collectEthMultiple(
                                                ethSniperPrivateKeys.map((pk) => new Wallet(pk, ethProvider)),
                                                new Wallet(ethDisperserPrivateKey, ethProvider).address,
                                                ethProvider,
                                            );
                                        }}
                                    >
                                        Collect All ETH <br />
                                        To Disperse Wallet
                                    </Form.Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>
            </div>
            <div className='min-w-[363px] flex flex-col border border-midnight'>
                <Section
                    icon={<SolanaIcon className='size-[20px]' />}
                    title={<span className='font-bold'>Solana Defaults</span>}
                    bodyClassName='w-full p-[17px]'
                >
                    <form className='flex flex-col'>
                        <div className='flex items-center mb-[8px]'>
                            <span className='w-[45px] text-right mr-[21px]'>Dev Buy</span>
                            <ArrowRightIcon className='size-[24px] mr-[14px] cursor-pointer' />
                            <Form.Float
                                value={solInitialBuyArrow}
                                onChange={(e) => onSolInitialBuyArrowChange(e.target.value)}
                                className='min-w-[46px] h-[31px] mr-[10px]'
                            />
                            <span className='w-[30px] mr-[23px]'>SOL</span>
                            <PlusIcon className='size-[24px] mr-[14px]' />
                            <Form.Float
                                value={solInitialBuyCopy}
                                onChange={(e) => onSolInitialBuyCopyChange(e.target.value)}
                                className='min-w-[46px] h-[31px] mr-[10px]'
                            />
                            <span>SOL</span>
                        </div>
                        <div className='flex items-center mb-[32px]'>
                            <span className='w-[45px] text-right mr-[21px]'>Snipe</span>
                            <ArrowRightIcon className='size-[24px] mr-[14px] cursor-pointer' />
                            <Form.Float
                                value={solSnipeArrow}
                                onChange={(e) => onSolSnipeArrowChange(e.target.value)}
                                className='min-w-[46px] h-[31px] mr-[10px]'
                            />
                            <span className='w-[30px] mr-[23px]'>SOL</span>
                            <PlusIcon className='size-[24px] mr-[14px]' />
                            <Form.Float
                                value={solSnipeCopy}
                                onChange={(e) => onSolSnipeCopyChange(e.target.value)}
                                className='min-w-[46px] h-[31px] mr-[10px]'
                            />
                            <span>SOL</span>
                        </div>
                        <div className='flex items-center'>
                            <span className='w-[45px] text-right flex flex-col mr-[21px]'>Auto Sell</span>
                            <div className='flex-1 flex flex-col gap-[8px]'>
                                <div className='flex'>
                                    <ArrowRightIcon className='size-[24px] mr-[14px] cursor-pointer' />
                                    <Form.Float
                                        value={autoSellDelayArrow}
                                        onChange={(e) => onAutoSellDelayArrowChange(e.target.value)}
                                        className='min-w-[46px] h-[31px] mr-[10px]'
                                    />
                                    <span className='w-[30px] mr-[23px]'>sec</span>
                                    <PlusIcon className='size-[24px] mr-[14px]' />
                                    <Form.Float
                                        value={autoSellDelayCreation}
                                        onChange={(e) => onAutoSellDelayCreationChange(e.target.value)}
                                        className='min-w-[46px] h-[31px] mr-[10px]'
                                    />
                                    <span>sec</span>
                                </div>
                                <div className='flex'>
                                    <ArrowRightIcon className='size-[24px] mr-[14px] cursor-pointer' />
                                    <Form.Float
                                        value={autoSellProfitArrow}
                                        onChange={(e) => onAutoSellProfitArrowChange(e.target.value)}
                                        className='min-w-[46px] h-[31px] mr-[10px]'
                                    />
                                    <span className='w-[30px] mr-[23px]'>
                                        %<br />
                                        profit
                                    </span>
                                    <PlusIcon className='size-[24px] mr-[14px]' />
                                    <Form.Float
                                        value={autoSellProfitCreation}
                                        onChange={(e) => onAutoSellProfitCreationChange(e.target.value)}
                                        className='min-w-[46px] h-[31px] mr-[10px]'
                                    />
                                    <span className='w-[30px] mr-[23px]'>
                                        %<br />
                                        profit
                                    </span>
                                </div>
                            </div>
                        </div>
                    </form>
                </Section>
                <Section
                    icon={<EthereumIcon className='size-[20px] filter invert' />}
                    title={<span className='font-bold'>Ethereum Defaults</span>}
                    bodyClassName='w-full p-[17px] pr-0'
                >
                    <form className='flex flex-col'>
                        <div className='flex items-center overflow-visible'>
                            <span className='w-[45px] text-right mr-[21px]'>Initial liquidity</span>
                            <ArrowRightIcon className='size-[24px] mr-[14px] cursor-pointer' />
                            <Form.Float
                                value={ethLiquidityArrow}
                                onChange={(e) => onEthLiquidityArrowChange(e.target.value)}
                                className='min-w-[46px] h-[31px] mr-[10px]'
                            />
                            <span className='w-[30px] mr-[23px]'>ETH</span>
                            <PlusIcon className='size-[24px] mr-[14px]' />
                            <Form.Float
                                value={ethLiquidityCopy}
                                onChange={(e) => onEthLiquidityCopyChange(e.target.value)}
                                className='min-w-[46px] h-[31px] mr-[10px]'
                            />
                            <span className='w-min'>ETH</span>
                        </div>
                    </form>
                </Section>
            </div>
        </div>
    );
}

function SaveButton(props: React.PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement>>) {
    return (
        <Form.Button className='w-[54px] h-[34px] text-[14px] text-ash-grey font-bold' {...props}>
            Save
        </Form.Button>
    );
}

function ClearButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <Form.Button className='w-[54px] h-[34px] text-[14px] text-ash-grey font-bold' {...props}>
            Clear
        </Form.Button>
    );
}
